import { ref, computed, watchEffect, watch } from 'vue'
import { defineStore } from 'pinia';
import { useDebounceFn } from '@vueuse/core'
import { useSessionStore } from '@/stores/SessionStore'
import { useMapAnimationStore } from "@/stores/MapAnimationStore";
import { useProductStore } from "@/stores/ProductStore";
import { useForecastModelStore } from '@/stores/ForecastModelStore';
import { useMapAdStore } from '@/stores/MapAdStore'
import { useProviderStore } from '@/stores/ProviderStore'
import { useMapUIStore } from '@/stores/MapUIStore'
import { useUserStore } from '@/stores/UserStore'
import { useCachedFetch } from '@/composables/useFetch'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { useUpdateKey } from '@/composables/useUpdateKey'
import baseOverlays from '@/data/baseOverlays.json';

import useSettingsGlobals from '@/composables/useSettingsGlobals'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'
import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals'
import StatsContent from '@/components/StatsContent.vue';

const { $STORM_INFO } = useSettingsGlobals();
const { $BASEURL } = useConfigurationGlobals()
const { $ALLOW_FORECAST_MAP_OBS_MARKERS } = useMapConfigurationGlobals()

export let useMapStore = defineStore('mapStore', () => {
    const sessionStore = useSessionStore();
    const mapAnimationStore = useMapAnimationStore();
    const productStore = useProductStore();
    const forecastModelStore = useForecastModelStore();
    const mapAdStore = useMapAdStore();
    const providerStore = useProviderStore();
    const userStore = useUserStore()
    const mapUIStore = useMapUIStore();
    const systemThemeIsDark = useMediaQuery('(prefers-color-scheme: dark)')

    const updateKey = useUpdateKey();

    let state = ref({
      id: 2,
      name: 'Wind Forecast',
      shortName: 'Wind Forecast',
      description: 'Computer generated, numerical model wind forecasts.',
      mapBackground: { name: 'High Contrast', usesThemes: true, theme: 'system' },
      geoInfo: null,
      animationControlsNeeded: false,
      customLayers: new Set(),
      forecastModel: null,
      forecastModelId: null,
      fxMapOpacity: 100,
      fxModelRun: null,
      fxOverlays: new Set(),
      forecastMapObsMarkersOverride: false,
      fxParam: 1,
      obsParam: 1,
      globalOverlays: new Set(),
      lat: null,
      lon: null,
      markerData: new Set([1]),
      mesonetList: [],
      mesonetListProviders: [],
      mesonetFilterType: null,
      obsOverlays: new Set(),
      overlays: {},
      searchType: 'search',
      settings: {},
      showNowCastSpots: false,
      showPointsOfInterest: false,
      spotTypes: new Set([1, 100, 101]),
      stormInfo: ref($STORM_INFO),
      stormPrintOnly: false,
      providers: [],
      useProfileId: false
    })

    let mapMode = ref('obs');
    
    let bounds = ref({
      latMin: null,
      lonMin: null,
      latMax: null,
      lonMax: null,
    });

    const initialView = ref(null);
    const initialViewLoaded = ref(false);

    // Convert the state providers field to the provider field needed for the API
    const convertedProvidersToMapView = computed(() => {
      if (state.value.providers.length === 0) return [-10001]

      return state.value.providers.map((provider) => providerStore.combinedProviders.find(p => p.id === provider)['provider_id']);
    });

    // Convert the state mesonetList field to the mesonetList field needed for the API
    const convertedMesonetListToMapView = computed(() => {
      if (state.value.mesonetList.length === 0) return ['none'];

      return state.value.mesonetList;
    });

    const convertProvidersFromMapView = async (providersIdArray) => {
      if (!providerStore.allProviders) await providerStore.getAllProviders()

      if (providersIdArray.indexOf(-10001) > -1) return providersIdArray;

      return providersIdArray.map(provider => {
        if (!provider) return null;
        return providerStore.combinedProviders.find(p => p.provider_id?.join(',') === provider.join(','))?.id
      }).filter(provider => provider !== null);
    };

    const convertMesonetListProvidersFromMapView = async (mesonetListArray) => {
      if (!providerStore.allProviders) await providerStore.getAllProviders()
      const providers = [];

      mesonetListArray.forEach(mesonet => providers.add(mesonet.split(':')[0]));

      return providers;
    };

    const convertProvidersFromApi = (providersArray) => {
      state.value.providers = providersArray.map((provider) => provider.id);
    }

    const convertMesonetListFromAPI = (mesonetListArray) => {
      const mesonetList = []
      const mesonetListProviders = [];
      
      mesonetListArray.forEach((mesonet) => {
        mesonetListProviders.push(mesonet.name)
        
        mesonet.mesonet_names.map((mesonetName) =>
          mesonetList.push(`${mesonet.name}:${mesonetName}`)
      )
    })

      state.value.mesonetListProviders = mesonetListProviders
      state.value.mesonetList = mesonetList
    };

    const mapBackgroundName = computed(() => {
      let backgroundName = state.value.mapBackground.name;
      
      if (state.value.mapBackground.usesThemes) {
        const uiTheme = mapUIStore.mapBackgroundTheme || 'system';
        let uiThemeName = uiTheme.charAt(0).toUpperCase() + uiTheme.slice(1);
        
        if (uiTheme.toLowerCase() === 'system') {
          uiThemeName = systemThemeIsDark.value ? 'Dark' : 'Light';
        }

        backgroundName = `${state.value.mapBackground.name} ${uiThemeName}`
      }

      return backgroundName;
   });

    const mapStateObject = computed(() => ({
      description: state.value.description,
      forecastModelId: state.value.forecastModelId,
      customLayers: [...state.value.customLayers],
      fxMapOpacity: state.value.fxMapOpacity,
      fxModelRun: state.value.fxModelRun,
      fxOverlays: [...state.value.fxOverlays],
      fxParam: state.value.fxParam,
      globalOverlays: [...state.value.globalOverlays],
      mapBackground: mapBackgroundName.value,
      mapMode: mapMode.value,
      markerData: [...state.value.markerData],
      mesonetFilterType: state.value.mesonetFilterType,
      mesonet_list: convertedMesonetListToMapView.value,
      name: state.value.name,
      obsOverlays: [...state.value.obsOverlays],
      obsParam: state.value.obsParam,
      overlays: { ...state.value.overlays },
      profileId: userStore.currentProfileId,
      provider: convertedProvidersToMapView.value,
      searchType: state.value.searchType,
      settings: { ...state.value.settings },
      spotTypes: [...state.value.spotTypes],
      useProfileId: state.value.useProfileId,
    }))

    async function mapProductUpdate(view, updateState = true) {
      const detail = {
        viewName: view.id,
        view: view,
        autoSelect: false
      };

      if (view.mapMode === 'fx' && view.overlays.hasOwnProperty('fx')) {
        const { selectedModel } =
          state.value.fxParam === 'currents'
            ? await forecastModelStore.getCurrentModels()
            : await forecastModelStore.getForecastModels(-1, bounds.value);

        state.value.forecastModelId = selectedModel.model_id
        view.overlays.fx.modelId = state.value.forecastModelId
      }

      if (updateState) updateMapStateWithMapView(view);

      detail.state = mapStateObject.value;

      window.$EventBus.dispatchEvent('map-product-update', detail);

      mapProductUpdateCleanup();
    }

    function mapProductUpdateCleanup() {
      delete state.value.overlays['dsat-operating-condition']?.fitBounds
    }    

    async function updateMapStateWithMapView(view) {
      if (!view) return;
      // If view does not have the id get it from the productStore

      state.value.id = view.id || productStore.getProductIdByName(view.name);

      state.value.name = view.name;
      state.value.shortName = Object.prototype.hasOwnProperty.call(view, 'shortName') ? view.shortName : '';
      state.value.description = view.description;
      state.value.overlays = {...view.overlays};
      state.value.lat = Object.prototype.hasOwnProperty.call(view, 'lat') ? view.lat : '';
      state.value.lon = Object.prototype.hasOwnProperty.call(view, 'lon') ? view.lon : '';
      state.value.analyticsName = view.analyticsName;

      if (view.mapBackground) setMapBackground(view.mapBackground);

      if ( view.overlayName && view.overlayName === 'lightning' ) {
        // If the lightning premium map product is not available load the wind map.
        // This means they do not have access to the lightning map.
        // This only applies to ops console.
        const productId = productStore.getProductIdByOverlayName(view.overlayName);
        
        if (!productStore.isProductAvailable(productId)) {
          const product = productStore.getProductFromAllProductsList(1);

          mapProductUpdate(product, true);
  
          return
        }
      }

      const overlays = Object.keys(state.value.overlays);
      const obsOverlays = new Set();
      const fxOverlays = new Set();
      
      state.value.settings = {};

      for (let overlay of overlays) {
        state.value.settings[overlay] = state.value.overlays[overlay];
        
        if (!Object.prototype.hasOwnProperty.call(baseOverlays, overlay)) continue;

        const mode = baseOverlays[overlay].info.mode;
        
        if (mode === 'obs') obsOverlays.add(overlay);
        if (mode === 'fx') fxOverlays.add(overlay);

        if (mode === 'both') {
          if (view.mapMode.toLowerCase() === 'fx') {
            fxOverlays.add(overlay);
          }

          if (view.mapMode.toLowerCase() === 'obs') {
            obsOverlays.add(overlay);
          }
        }
        
      }

      state.value.obsOverlays = obsOverlays;
      state.value.fxOverlays = fxOverlays;
      state.value.forecastMapObsMarkersOverride = view.forecastMapObsMarkersOverride || false

      if (view.mapMode.toLowerCase() !== mapMode.value) {
        mapMode.value = view.mapMode.toLowerCase();
      }
      
      if (view.mapMode.toLowerCase() === 'fx') {
        state.value.fxParam = view.id === 13 ? 'currents' : getFxParamFromViewOverlays(view.overlays);
        state.value.forecastSelectorNeeded = Object.prototype.hasOwnProperty.call(view, 'forecastSelectorNeeded') ? view.forecastSelectorNeeded : true; 
        state.value.fxMapOpacity = 100;
        
        const fxModel = getFxModelFromViewOverlays(view.overlays);
        state.value.forecastModelId = fxModel;
      }

      if (Object.prototype.hasOwnProperty.call(view, 'animationControlsNeeded')) {
        state.value.animationControlsNeeded = view.animationControlsNeeded;
      } else if (view.mapMode.toLowerCase() === 'fx') {
        state.value.animationControlsNeeded = true;
      } else {
        delete state.value.animationControlsNeeded
      }

      if (Object.prototype.hasOwnProperty.call(view.overlays, 'markers')) {
        state.value.markerData = new Set(view.overlays.markers.data)
        state.value.spotTypes = view.overlays.markers.loc.length === 0 ? new Set(view.overlays.markers.data) : new Set(view.overlays.markers.loc);
        state.value.obsParam = view.overlays.markers.data[0];
        state.value.searchType = view.overlays.markers.searchType || 'search';

        if (Object.prototype.hasOwnProperty.call(view.overlays.markers, 'profileId')) {
          state.value.useProfileId = true;

          if (view.overlays.markers.profileId) userStore.setCurrentProfile(view.overlays.markers.profileId)
        } else {
          state.value.useProfileId = false;
        }
      } else {
        state.value.spotTypes = new Set();
        state.value.searchType = null;
      }

      // Spot type 101 if spot type 100 is present
      state.value.markerData.has(100) && state.value.markerData.add(101);
      state.value.spotTypes.has(100) && state.value.spotTypes.add(101);

      state.value.showPointsOfInterest = !state.value.markerData.has(100) && state.value.spotTypes.has(100);
      state.value.showNowCastSpots = state.value.markerData.has(100) && state.value.spotTypes.has(100);

      if (view.overlays.markers?.provider) {
        state.value.providers = await convertProvidersFromMapView(view.overlays.markers.provider);
      }

      if (view.overlays.markers?.mesonet_list) {
        state.value.mesonetList = view.overlays.markers?.mesonet_list;
        state.value.mesonetListProviders = await convertMesonetListProvidersFromMapView(view.overlays.markers.mesonet_list);
        state.value.mesonetFilterType = 'mesonet_list';
        state.value.stormPrintOnly = true;
      }

      if (Object.prototype.hasOwnProperty.call(view.overlays, 'nautical')) {
        state.value.globalOverlays.add('nautical')
      } else {
        state.value.globalOverlays.delete('nautical')
      }

      if (Object.prototype.hasOwnProperty.call(view, 'customLayers')) {
        state.value.customLayers = new Set();
        view.customLayers.forEach(id => state.value.customLayers.add(id));
      }

      if (Object.prototype.hasOwnProperty.call(view.overlays, 'precip-accumulation')) {
        state.value.settings['precip-accumulation'] = {
          period: view.overlays['precip-accumulation'].period
        };
      }

      mapAnimationStore.clearAnimationFrames();

      if (
        ($ALLOW_FORECAST_MAP_OBS_MARKERS || state.value.forecastMapObsMarkersOverride) &&
        Object.prototype.hasOwnProperty.call(view.overlays, 'markers') &&
        view.mapMode.toLowerCase() === 'fx'
      ) {
        mapAnimationStore.setTimeseriesSettings({ start: 'now-past' })
      } else if (Object.prototype.hasOwnProperty.call(view, 'timeseries')) {
        mapAnimationStore.setTimeseriesSettings(view.timeseries)
      } else {
        mapAnimationStore.resetTimeseriesSettings()
      }
    }

    async function debouncedCustomizedMapUpdate(updateType) {
      state.value.name = 'Customized Map'
      state.value.shortName = 'Customized'
      state.value.id = 'unsaved'
      state.value.description = 'A custom observation view.'

      updateType = updateType === 'global' || updateType === 'custom-layers' ? updateType : mapMode.value

      if (state.value.fxParam === 'currents') {
        await forecastModelStore.getCurrentModels();
      }

      if (mapMode.value === 'fx') {
        state.value.forecastSelectorNeeded = true

        if (
          (!$ALLOW_FORECAST_MAP_OBS_MARKERS && !state.value.forecastMapObsMarkersOverride) ||
          (!state.value.obsOverlays.has('markers') && !state.value.fxOverlays.has('markers'))
        ) {
          clearObsData()
        }

        if (($ALLOW_FORECAST_MAP_OBS_MARKERS || state.value.forecastMapObsMarkersOverride) && state.value.obsOverlays.has('markers')) {
          mapAnimationStore.setTimeseriesSettings({ start: 'now-past' })
        } else {
          mapAnimationStore.resetTimeseriesSettings()
        }
      } else {
        mapAnimationStore.resetTimeseriesSettings()
      }

      const settings = { updateType, state: mapStateObject.value }

      window.$EventBus.dispatchEvent('map-customization-update', settings)
    }

    const customizedMapUpdate = useDebounceFn((updateType) => {
      debouncedCustomizedMapUpdate(updateType)
    }, 1000);
    
    function mapSettingsUpdate() {
      const settings = { state: mapStateObject.value }

      window.$EventBus.dispatchEvent('map-settings-update', settings)      
    }

    function deactivateSpotType(propName) {state.value.obsOverlays
      state.value[propName] = false;
    }

    function clearObsData() {
      state.value.obsOverlays = new Set();
      state.value.spotTypes = new Set();
      state.value.markerData = new Set();
      state.value.showPointsOfInterest = false;
      state.value.showNowCastSpots = false;
    }

    function updateSpotTypes() {
      if (state.value.showPointsOfInterest || state.value.showNowCastSpots) {
        state.value.spotTypes.add(100);
        state.value.spotTypes.add(101);
      }

      if (!state.value.showPointsOfInterest && !state.value.showNowCastSpots) {
        state.value.spotTypes.delete(100);
        state.value.spotTypes.delete(101);
      }

      if (state.value.spotTypes.size === 0) {
        state.value.obsOverlays.delete('markers');
      }
    }

    function updateMesonetFilterType() {
      state.value.mesonetFilterType = state.value.stormPrintOnly ? 'mesonet_list' : 'provider';
    }

    function enableMesonetFilter() {
      state.value.spotTypes.add(1);
      updateMesonetFilterType();
    }
    
    function disableMesonetFilter() {
      state.value.spotTypes.delete(1);
      state.value.mesonetFilterType = null;
    }

    function toggleMesonetFilter(enabled) {
      if (enabled) {
        enableMesonetFilter();
      } else {
        disableMesonetFilter();
      }
    }

    function getFxParamFromViewOverlays(viewOverlays) {
      return !viewOverlays || !viewOverlays['fx'] ? 1 : viewOverlays['fx']?.param      
    }

    function getFxModelFromViewOverlays(viewOverlays) {
      return !viewOverlays || !viewOverlays['fx']?.modelId
        ? -1
        : viewOverlays['fx']?.modelId      
    }

    function updateMapMode(mode) {
      if (mode === 'fx') {
        if (
          (!$ALLOW_FORECAST_MAP_OBS_MARKERS && !state.value.forecastMapObsMarkersOverride) ||
          (!state.value.obsOverlays.has('markers') && !state.value.fxOverlays.has('markers'))
        ) {
          clearObsData()
        }
      } else {
        if (state.value.obsOverlays.size === 0) {
          updateMapStateWithMapView(productStore.getProduct('1'))
        }
      }
    }

    function getBounds() {
      if (!window.$JWX.hasOwnProperty('map')) {
        console.warn('JWeather Map not ready yet.');

        return
      }
      
      const newBounds = window.$JWX.map.getBounds();

      if (!newBounds) return;

      bounds.value.latMin = newBounds.getSouthWest().lat();
      bounds.value.lonMin = newBounds.getSouthWest().lng();
      bounds.value.latMax = newBounds.getNorthEast().lat();
      bounds.value.lonMax = newBounds.getNorthEast().lng();

      return {
        latMin: bounds.value.latMin,
        lonMin: bounds.value.lonMin,
        latMax: bounds.value.latMax,
        lonMax: bounds.value.lonMax
      }
    }    

    function updateFavoriteSpotsMapId(profileId) {
      const product = productStore.getProduct(104)

      product.overlays.markers.profileId = profileId;

      customizedMapUpdate('obs');
    }

   function loadInitialView() {
    if (!initialView.value || initialViewLoaded.value) return

    initialViewLoaded.value = true;

     (async () => {
        await productStore.getRegionalProducts();

        // Check if the user is authorized to view overlays in the initial view
        // Overlays that a user is not authorized to view will be removed from the initial view
        // and sent back to Cosmos.js to load the map without those overlays.
        // This situation can occur when an authorized user logs out of an account with which 
        // has access to these layers and a non-authorized user logs in.
        // Or when sharing a url that contains overlays the current user is not authorized to view.
        // For now this only occurs with Ops Console.
        const initialViewOverlays = {...initialView.value.overlays} || {};
        const overlayNames = Object.keys(initialViewOverlays || {})
        const overlaysToCheckForAuthorization = ['lightning', 'dsat-operating-condition']
        let productRemoved = false;

        overlayNames.forEach(overlayName => {
          if (overlaysToCheckForAuthorization.includes(overlayName) && !productStore.isProductAvailableByOverlayName(overlayName)) {
            delete initialViewOverlays[overlayName]
            productRemoved = true;
          }
        });

        if (productRemoved) {
          initialView.value = {
            ...initialView.value,
            overlays: initialViewOverlays
          }

          if (initialViewOverlays.length === 0) {
            const markersOverly = productStore.getProduct(1);

            mapProductUpdate(markersOverly);
          } else {
            updateMapStateWithMapView(initialView.value);
            customizedMapUpdate(initialView.value.mapMode);
          }
        } else {
          updateMapStateWithMapView(initialView.value);
        }

        initialView.value = null
        
        window.$EventBus.dispatchEvent('is-animation-control-needed');
      })()
    }

    async function getMapGeoInfo() {
      if (!window.$JWX.hasOwnProperty('map')) {
        console.warn('JWeather Map not ready yet.')

        return
      }

      const center = window.$JWX.map.getCenter()

      if (!center) return

      const params = {
        lat: center.lat(),
        lon: center.lng(),
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString();
      const url = `${$BASEURL}/common/getGeoInfo?${queryString}`;

      try {
        const { error, data } = await useCachedFetch(url)

        if (error.value) {
          throw error.value
        }

        if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
          throw data.value.status || data.value.status_code
        }

        state.value.geoInfo = data.value;

        return data;
      } catch (error) {
        console.warn(error)

        return error
      }
    }

    function setMapBackground(background = state.value.mapBackground.name) {
      const mapBackground = { 
        name: background,
        usesThemes: false,
        theme: mapUIStore.mapBackgroundTheme || null
       };

      if (background.indexOf('High Contrast') > -1) {
        mapBackground.name = 'High Contrast';
        mapBackground.usesThemes = true;
        mapBackground.theme = mapUIStore.mapBackgroundTheme || 'system';

        if (background === 'High Contrast') {
          mapBackground.theme = 'light';
          mapUIStore.mapBackgroundTheme = 'light';
          mapSettingsUpdate();
        }
      }

      mapUIStore.updateMapBackgroundTheme();

      state.value.mapBackground = mapBackground;
    }
    
    watch(mapMode, () => {
      updateMapMode(mapMode.value)
    })

    watchEffect(() => {
      if (state.value.showPointsOfInterest) {
        deactivateSpotType('showNowCastSpots');
      }

      updateSpotTypes();
    });

    watchEffect(() => {
      if (state.value.showNowCastSpots) {
        deactivateSpotType('showPointsOfInterest');
        state.value.markerData.add(100)
        state.value.markerData.add(101)
      } else {
        state.value.markerData.delete(100)
        state.value.markerData.delete(101)
      }  
      
      updateSpotTypes();
    });

    watchEffect(() => {
      if (!state.value.spotTypes.has(1)) {
        state.value.markerData.delete(1);
      } else {
        state.value.markerData.add(1);
        state.value.obsOverlays.add('markers');
      }

      updateSpotTypes();
    });

    watchEffect(() => {
      if (state.value.obsOverlays.has('onsite-reports')) {
        state.value.settings['onsiteReports'] = 1440

        return
      }
      
      if (Object.prototype.hasOwnProperty.call(state.value.settings, 'onsiteReports')) {
        delete state.value.settings.onsiteReports;
      }
    });

    watchEffect(() => {
      mapAdStore.changeAdSlot(state.value.id);
    })   

    // If markers are allowed on the map and map is not favorited or is an unsaved map while in fx mode:
    // Turning on the fx map while markers are on the map, or vice versa, will switch the obsParam to the fxParam.
    // Changing the fxParam will also change the obsParam to the fxParam.
    // Switching the obsParam will not change the fxParam, but still show the selected obsParam.
    watchEffect(() => {
      if (!$ALLOW_FORECAST_MAP_OBS_MARKERS && !state.value.forecastMapObsMarkersOverride) return;

      if (
        (state.value.id < 999 || state.value.id === 'unsaved') &&
        mapMode.value === 'fx' &&
        state.value.overlays['markers']
      ) {
        state.value.obsParam = state.value.fxParam
      }
    });

    watch(updateKey.key, () => {
      // Gate on actual map readiness rather than the key's initial 0 value. The
      // key is a shared singleton starting at 0; for a returning user (units
      // already in localStorage) init never advances it, so `oldKey === 0` would
      // swallow their first real unit change. `initialViewLoaded` is set once the
      // map is up, so this still skips the startup bump but lets every user change
      // through.
      if (!initialViewLoaded.value) return;

      if (state.value.name.toLowerCase().includes('unsaved') || state.value.name.toLowerCase().includes('customized')) {
        customizedMapUpdate();
      } else {
        // The current product can be missing from productList when regional
        // products haven't loaded (or failed to load) for the current bounds.
        const mapProduct = productStore.getProduct(parseInt(state.value.id));

        if (mapProduct) mapProductUpdate(mapProduct)
      }
    })

    window.$EventBus.addEventListener('cosmos-init-view', async (e) => {
      initialView.value = JSON.parse(JSON.stringify(e.detail.view))
    })

    window.$EventBus.addEventListener('map-hash-changed', (e) => {
      updateMapStateWithMapView(e.detail.view)
    })

    window.$EventBus.addEventListener('map-geo-info-changed', (e) => {
      const geoInfo = e.detail.data;
      geoInfo.local_utc_offset = `${e.detail.data.local_utc_offset.slice(0, -2)}:00`;
      state.value.geoInfo = geoInfo;

      getBounds();
      productStore.getRegionalProducts();
    });

    window.$EventBus.addEventListener('cosmos-precip-accum-timestamp', (e) => {
      const timestamp = e.detail.timestamp;
      state.value.settings = {
        ...state.value.settings,
        'precip-accumulation': {
          ...state.value.settings['precip-accumulation'],
          latestTimestamp: timestamp
        }
      };
    });

    window.$EventBus.addEventListener('cosmos-map-layer-event-relay', (e) => {
      //NOTE: Workaround/hack to update the view used by JW Cosmos to enable better map state saving for layers that use map dashboards.
      // Uses JWX(Vue) MapStore as a relay.
      state.value.settings = {
        ...state.value.settings,
        [e.detail.layer]: {
          ...state.value.settings[e.detail.layer],
          ...e.detail.settings
        }
      }

      customizedMapUpdate(e.detail.mode)
    });

    // Restore the persisted map background before announcing the initial map state.
    // The persisted High Contrast name has no theme suffix; add it so setMapBackground
    // does not hit the bare 'High Contrast' branch that forces the light theme.
    const savedTheme = mapUIStore.mapBackgroundTheme || 'system';
    const savedBackground = mapUIStore.mapBackground === 'High Contrast'
      ? `High Contrast ${savedTheme.charAt(0).toUpperCase() + savedTheme.slice(1)}`
      : mapUIStore.mapBackground;

    if (savedBackground) setMapBackground(savedBackground);

    window.$EventBus.dispatchEvent('map-state-init', { state: mapStateObject.value });
    window.$JWX.initialMapState = mapStateObject.value;

    return {
      bounds,
      mapMode,
      state,
      mapBackgroundName,
      convertMesonetListFromAPI,
      convertMesonetListProvidersFromMapView,
      convertProvidersFromApi,
      customizedMapUpdate,
      disableMesonetFilter,
      enableMesonetFilter,
      getBounds,
      getMapGeoInfo,
      loadInitialView,
      mapProductUpdate,
      mapSettingsUpdate,
      toggleMesonetFilter,
      updateFavoriteSpotsMapId,
      updateMapMode,
      updateMapStateWithMapView,
      updateMesonetFilterType,
      updateSpotTypes,
    }
})
